import {
    ItemWithEditable,
    Metric,
    ProductPPG,
    PromoConfigDictionary,
    PromoEvent,
    PromoLibraryKpiConfigType,
    PromoLibraryKpiStorageType,
    PromoTactic,
} from 'types/tradePromoOptimization';
import { sendNotification } from 'utils/helperMethods';
import { Parser } from 'expr-eval';
import { PromotionTypes, tpoPromoMetricKeys } from 'utils/Enums';
import dayjs from 'dayjs';
import _ from 'lodash';
import { PromoInfoFormFieldNames, PromoStatus, PublishStatus, TpoMessages } from 'constants/tpo';
import utc from 'dayjs/plugin/utc';
import { tpoLogger } from 'utils/logger/tpoLogger';
dayjs.extend(utc);

const parser = new Parser();

export const formatKpiOptionsForReset = (
    kpiFilterDropdownValues: Array<{ label: string; value: string; isEditable?: boolean }> | undefined,
): Array<{ label: string; value: string }> => {
    if (!kpiFilterDropdownValues?.length) return [];
    return (
        kpiFilterDropdownValues
            .filter((kpi) => kpi.isEditable)
            .map((kpi) => ({
                label: kpi.label,
                value: kpi.value,
            })) || []
    );
};

export const getCustomerSelectionCounts = (filters: Record<string, any> | undefined) => {
    if (!filters) return { inclusionCount: 0, exclusionCount: 0 };

    const dim2Filters = filters.dim2;
    if (!dim2Filters) return { inclusionCount: 0, exclusionCount: 0 };

    const inclusion = dim2Filters.inclusion ?? [];
    const exclusion = dim2Filters.exclusion ?? [];

    if (exclusion.length > 0) {
        const exclusionMatches = exclusion.filter((item: any) => item.attributeType === 'customer_name');
        if (exclusionMatches.length > 0) {
            return { inclusionCount: 0, exclusionCount: exclusionMatches.length };
        }
    }

    const inclusionMatches = inclusion.filter((item: any) => item.attributeType === 'customer_name');
    const inclusionCount = inclusionMatches.length;

    return { inclusionCount, exclusionCount: 0 };
};

export const validateSingleCustomerSelection = (filters: Record<string, any> | undefined): boolean => {
    const { inclusionCount, exclusionCount } = getCustomerSelectionCounts(filters);

    if (exclusionCount > 0 || inclusionCount > 1) {
        sendNotification('error', 'Please select only one customer');
        return false;
    }

    if (inclusionCount === 0) {
        sendNotification('error', 'Please select at least one customer');
        return false;
    }

    return true;
};

export const formatSelectedKpis = (
    kpiIds: string[],
    kpiConfigData: PromoLibraryKpiConfigType | null,
): Array<{ key: string; label: string; storage_type: PromoLibraryKpiStorageType }> => {
    if (!kpiIds?.length || !kpiConfigData) return [];

    return kpiIds
        .map((kpiId) => {
            const kpiConfig = kpiConfigData?.[kpiId];
            if (!kpiConfig) return null;

            return {
                key: kpiId,
                label: kpiConfig.label,
                storage_type: kpiConfig.storageType,
                is_editable: kpiConfig.isEditable,
            };
        })
        .filter(
            (
                kpi,
            ): kpi is { key: string; label: string; storage_type: PromoLibraryKpiStorageType; is_editable: boolean } =>
                kpi !== null,
        );
};
export const buildResetPayload = (
    libraryGroup: string,
    selectedKpiIds: string[],
    kpiConfigData: PromoLibraryKpiConfigType | null,
    filters: Record<string, any> | undefined,
    resetOption?: 'filter_items' | 'all',
) => {
    const selectedKpis = formatSelectedKpis(selectedKpiIds, kpiConfigData);

    let processedFilters = filters || {};

    // When resetOption is 'all', exclude dim1 and dim10 from filters
    if (resetOption === 'all' && processedFilters) {
        processedFilters = { ...processedFilters };
        delete processedFilters.dim1;
        delete processedFilters.dim10;
    }

    return {
        library_group: libraryGroup,
        kpis: selectedKpis,
        filters: processedFilters,
    };
};

export const isReservedOperationalPlanName = (
    planName: string,
    yearOfPlan: number,
    dim1Attributes: any[],
    dim2Attributes: any[],
): boolean => {
    const category = String(dim1Attributes?.[0] ?? '').trim();
    const customer = String(dim2Attributes?.[0] ?? '').trim();
    const baseReservedName = `${yearOfPlan}_${category}_${customer}`;
    const normalizedName = planName?.trim() ?? '';
    return normalizedName === baseReservedName || normalizedName === `${baseReservedName} TPO`;
};

const countCharacters = (str: string, char: string): number => str.split(char).length - 1;

export const handleMetricInputChange = (
    promoConfigDictionary: PromoConfigDictionary,
    selectedEventInfo: PromoEvent | null,
    metricId: number,
    productName: string,
    value: string,
    isMetric: boolean,
) => {
    if (!selectedEventInfo?.currentPromoTactic) return;
    const stringValue = String(value).trim();

    const { allowNegative, allowedDecimals, name, name_key } = isMetric
        ? promoConfigDictionary.promoMetrics[metricId]
        : promoConfigDictionary.spendMetrics[metricId];

    // Check for invalid characters (anything other than digits, '-', or '.')
    const invalidChars = stringValue.split('').filter((char) => !char.match(/[\d.-]/));
    if (invalidChars.length > 0) {
        sendNotification('error', `Invalid characters entered: ${invalidChars.join(', ')}`);
        return;
    }

    // Ensure '-' appears only at the beginning and is allowed
    const dashCount = countCharacters(stringValue, '-');
    if (dashCount > 1 || (dashCount === 1 && stringValue[0] !== '-')) {
        sendNotification('error', `${name} contains an invalid negative sign.`);
        return;
    }

    if (!allowNegative && stringValue.startsWith('-')) {
        sendNotification('error', `${name} can't be negative.`);
        return;
    }

    // Ensure only one decimal point
    const dotCount = countCharacters(stringValue, '.');
    if (dotCount > 1) {
        sendNotification('error', `${name} contains multiple decimal points.`);
        return;
    }

    // Validate decimal places
    if (allowedDecimals > 0) {
        const parts = stringValue.split('.');
        if (parts.length === 2 && parts[1].length > allowedDecimals) {
            sendNotification('error', `${name} can have up to ${allowedDecimals} decimal places only.`);
            return;
        }
    } else if (stringValue.includes('.')) {
        sendNotification('error', `${name} must be a whole number and cannot contain decimals.`);
        return;
    }

    const metricsKey = isMetric ? 'metrics' : 'spendDetails';

    // Update the selected event's promo tactic
    let updatedPPGs = selectedEventInfo.currentPromoTactic.ppgs.map((ppg: ProductPPG) => {
        if (ppg.name === productName) {
            return {
                ...ppg,
                [metricsKey]: ppg[metricsKey].map((metric) => {
                    if (metric.id === metricId) {
                        return { ...metric, value: stringValue }; // Keep as string for UI
                    }
                    return metric;
                }),
            };
        }
        return ppg;
    });

    // Update the selected event's promo tactic
    updatedPPGs = updatedPPGs.map((ppg: ProductPPG) => {
        // Compute affected KPIs dynamically (pass original string value)
        if (ppg.name === productName) {
            return computeCascadingKPIs(promoConfigDictionary, name_key, stringValue, ppg);
        }
        return ppg;
    });

    return updatedPPGs;
};

export const computeCascadingKPIs = (
    promoConfigDictionary: PromoConfigDictionary,
    updatedKpi: any,
    newValue: any,
    ppg: ProductPPG,
) => {
    const allMetricKpiValues: any = {};

    // Extract current KPI values from `ppgs`
    ppg.metrics.forEach((metric: Metric) => {
        allMetricKpiValues[promoConfigDictionary.promoMetrics[metric.id].name_key] = metric.value;
    });
    ppg.spendDetails.forEach((metric: Metric) => {
        allMetricKpiValues[promoConfigDictionary.spendMetrics[metric.id].name_key] = metric.value;
    });

    allMetricKpiValues[updatedKpi] = newValue;
    // console.log('Updating value for KPI - ', updatedKpi, newValue);

    // Recursive function to update all dependencies
    const updateDependencies = (kpi: string, level: number = 0) => {
        if (promoConfigDictionary.kpiDependencies[kpi]) {
            const dependents = promoConfigDictionary.kpiDependencies[kpi];

            tpoLogger.kpiDependencyChain(kpi, dependents, level);

            dependents.forEach((dependentKpi: any) => {
                // console.log('Updating value for KPI - ', dependentKpi);
                const formula = promoConfigDictionary.kpiFormulas[dependentKpi];
                if (formula) {
                    try {
                        // Extract variables from formula for better debugging
                        const variableMatches = formula.match(/\b[A-Za-z_]+\b/g) || [];
                        const variables: Record<string, any> = {};

                        // Ensure only valid numbers are used in the formula
                        const expression = formula.replace(/\b[A-Za-z_]+\b/g, (match: any) => {
                            const value = allMetricKpiValues[match];
                            const numericValue = value !== undefined && !isNaN(Number(value)) ? Number(value) : 0;
                            variables[match] = { original: value, used: numericValue };
                            return numericValue.toString();
                        });

                        let evaluatedValue = parser.evaluate(expression);

                        // Check for NaN or Infinity
                        if (!isFinite(evaluatedValue) || isNaN(evaluatedValue)) {
                            tpoLogger.warn(`Invalid calculation result for ${dependentKpi}, defaulting to 0`, {
                                formula,
                                expression,
                                result: evaluatedValue,
                            });
                            evaluatedValue = 0;
                        }

                        allMetricKpiValues[dependentKpi] = evaluatedValue;

                        // Beautiful formula evaluation logging
                        tpoLogger.formulaEvaluation(formula, variables, evaluatedValue);
                        tpoLogger.kpi(`KPI Updated: ${dependentKpi}`, evaluatedValue);

                        updateDependencies(dependentKpi, level + 1); // Recurse to handle cascading dependencies
                    } catch (error) {
                        tpoLogger.error(`Formula evaluation failed for ${dependentKpi}`, {
                            formula,
                            error: error instanceof Error ? error.message : error,
                            kpi: dependentKpi,
                        });
                    }
                }
            });
        }
    };

    updateDependencies(updatedKpi);

    const updateValues = (metrics: Metric[], getKey: (id: number) => string): Metric[] =>
        metrics.map((metric) => {
            const key = getKey(metric.id);
            if (key in allMetricKpiValues) {
                let value = allMetricKpiValues[key];
                if (isNaN(value)) {
                    value = '0';
                } else if (value === Infinity) {
                    value = Number.MAX_SAFE_INTEGER.toString(); // 9007199254740991
                } else if (value === -Infinity) {
                    value = (-Number.MAX_SAFE_INTEGER).toString(); // -9007199254740991
                }
                return {
                    ...metric,
                    value: value,
                };
            }
            return metric;
        });

    return {
        ...ppg,
        metrics: updateValues(ppg.metrics, (id) => promoConfigDictionary.promoMetrics[id].name_key),
        spendDetails: updateValues(ppg.spendDetails, (id) => promoConfigDictionary.spendMetrics[id].name_key),
    };
};

export function getPromoMetricByNameKeyName(
    promoConfig: PromoConfigDictionary,
    targetNameKey: tpoPromoMetricKeys,
): ItemWithEditable {
    const { promoMetrics, spendMetrics } = promoConfig;

    const promoKey = Object.keys(promoMetrics).find((key) => promoMetrics[Number(key)]?.name_key === targetNameKey);

    if (promoKey !== undefined) {
        return promoMetrics[Number(promoKey)];
    }

    const spendKey = Object.keys(spendMetrics).find((key) => spendMetrics[Number(key)]?.name_key === targetNameKey);

    return spendMetrics[Number(spendKey)];
}

export function getQuarter(unixDate: number): string {
    const month = dayjs.unix(unixDate).utc().month(); // 0-indexed: Jan = 0
    if (month <= 2) return 'Q1';
    if (month <= 5) return 'Q2';
    if (month <= 8) return 'Q3';
    return 'Q4';
}

export function isPromoYearChangeNotAllowed(
    originalPromoStartDate: number | null,
    currentPromoStartDate: number | null,
    status: string,
): boolean {
    if (!originalPromoStartDate || !currentPromoStartDate) return false;

    const originalYear = dayjs.unix(originalPromoStartDate).year();
    const currentYear = dayjs.unix(currentPromoStartDate).year();

    return status?.toLowerCase() === 'approved' && originalYear !== currentYear;
}

export const isValidMetricInput = (value: string, metric: ItemWithEditable, setWarningMessage: Function): boolean => {
    if (!value) return false;
    value = String(value).trim();

    // Check if it's a valid number
    const num = Number(value);
    if (isNaN(num)) return false;

    // Check negative
    if (!metric.allowNegative && num < 0) {
        setWarningMessage('Negative values not allowed.');
        return false;
    }

    // Check min value (only if minValue is not null)
    if (metric.minValue !== null && num < metric.minValue) {
        setWarningMessage(`Minimum allowed value is ${metric.minValue}.`);
        return false;
    }

    // Check max value (only if maxValue is not null)
    if (metric.maxValue !== null && num > metric.maxValue) {
        setWarningMessage(`Maximum allowed value is ${metric.maxValue}.`);
        return false;
    }

    // Check decimal precision
    const hasDecimal = value.includes('.');
    const decimalPart = value.split('.')[1] || '';

    if (metric.allowedDecimals === 0 && hasDecimal) {
        setWarningMessage('Decimal values are not allowed.');
        return false;
    }

    if (decimalPart.length > metric.allowedDecimals) {
        setWarningMessage(`Maximum ${metric.allowedDecimals} decimal place(s) allowed.`);
        return false;
    }
    setWarningMessage('');
    return true;
};

/**
 * Restrict input to numbers, optional dot and minus.
 * Prevents multiple dots or misplaced minus signs.
 */
export const handleNumberInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>, currentValue: string) => {
    const isNumber = e.key >= '0' && e.key <= '9';
    const allowedKeys = ['Backspace', 'Tab', 'ArrowLeft', 'ArrowRight', 'Delete', 'Enter', '.', '-'];
    const isCtrlA = (e.key === 'a' || e.key === 'A') && (e.ctrlKey || e.metaKey);

    if (!isNumber && !allowedKeys.includes(e.key) && !isCtrlA) {
        e.preventDefault();
    }

    // Prevent multiple dots
    if (e.key === '.' && currentValue.includes('.')) {
        e.preventDefault();
    }

    // Prevent multiple or misplaced minus signs
    if (e.key === '-' && (currentValue.includes('-') || currentValue.length > 0)) {
        e.preventDefault();
    }
};

export const handleRecommendSuccessDefaultValue = (
    updatedPPgs: ProductPPG[],
    promoConfigDictionary: PromoConfigDictionary,
) => {
    const newUpdatedPPGs = _.cloneDeep(updatedPPgs);

    // List of metric keys to process
    const metricKeys = [
        tpoPromoMetricKeys.SCAN_REDEMPTION_PERCENT,
        tpoPromoMetricKeys.TOTAL_PROMO_SPENDS,
        tpoPromoMetricKeys.NET_PRICE_INCL_MDF_PER_UNIT,
        tpoPromoMetricKeys.NET_PRICE_PER_STAT,
        tpoPromoMetricKeys.GROSS_MARGIN_DOLLARS,
        tpoPromoMetricKeys.CONSUMPTION_MPIC_STAT,
        tpoPromoMetricKeys.PROMOTED_AUR,
        tpoPromoMetricKeys.SCAN_VOLUME,
        tpoPromoMetricKeys.TPM_PLANNED_SHIPMENT_VOLUME,
    ];

    // console.log(
    //     'total Metrics to compute ',
    //     metricKeys.map((m) => m.toLocaleUpperCase()),
    // );

    newUpdatedPPGs.map((ppg: ProductPPG) => {
        const allMetricKpiValues: any = {};

        // Extract current KPI values from `ppgs`
        ppg.metrics.forEach((metric: Metric) => {
            allMetricKpiValues[promoConfigDictionary.promoMetrics[metric.id].name_key] = metric.value;
        });
        ppg.spendDetails.forEach((metric: Metric) => {
            allMetricKpiValues[promoConfigDictionary.spendMetrics[metric.id].name_key] = metric.value;
        });

        // console.log(`Computing for material group  -  ${ppg.name}`);
        // console.log('values object ', allMetricKpiValues);

        // Loop through each metric key and process it
        metricKeys.forEach((metricKey) => {
            const formula = promoConfigDictionary.kpiFormulas[metricKey];
            let evaluatedValue: any = 0;

            // console.log(`Computing default value for metricKey - ${metricKey}  ${metricKey.toLocaleUpperCase()}`);
            // console.log(`formula -  ${formula}`);
            if (formula) {
                try {
                    const expression = formula.replace(/\b[A-Za-z_]+\b/g, (match: any) => {
                        const value = allMetricKpiValues[match];
                        return Number(value) ? value.toString() : '0';
                    });
                    // console.log(`expression -  ${expression}`);

                    evaluatedValue = parser.evaluate(expression);

                    // Check for NaN or Infinity
                    if (!isFinite(evaluatedValue) || isNaN(evaluatedValue)) {
                        evaluatedValue = 0;
                    }

                    // Update metrics and spend details with the new calculated value
                    ppg.metrics.forEach((m: Metric) => {
                        const promoMetric = promoConfigDictionary.promoMetrics[m.id];
                        if (m.id === promoMetric.id && promoMetric.name_key === metricKey) {
                            m.value = evaluatedValue;
                            // console.log(`${promoMetric.name}  = ${evaluatedValue}`);
                        }
                    });
                    ppg.spendDetails.forEach((m: Metric) => {
                        const spendMetric = promoConfigDictionary.spendMetrics[m.id];
                        if (m.id === spendMetric.id && spendMetric.name_key === metricKey) {
                            m.value = evaluatedValue;
                            // console.log(`${spendMetric.name}  = ${evaluatedValue}`);
                        }
                    });
                    // console.log('--------------------------------');
                } catch (error) {
                    console.error(`Error evaluating formula for ${metricKey}`, error);
                }
            }
        });
    });

    return newUpdatedPPGs;
};

// temp changes to compute value
export const setComputedValuePostPromoDetailsCall = (
    updatedPPgs: ProductPPG[],
    promoConfigDictionary: PromoConfigDictionary,
) => {
    const newUpdatedPPGs = _.cloneDeep(updatedPPgs);
    // List of metric keys to process
    const metricKeys = [
        tpoPromoMetricKeys.PROMOTED_AUR,
        tpoPromoMetricKeys.TPM_PLANNED_SHIPMENT_VOLUME,
        tpoPromoMetricKeys.EDLP_SPENDS,
        tpoPromoMetricKeys.SCAN_VOLUME_ISOLATED,
        tpoPromoMetricKeys.TOTAL_PROMO_SPENDS,
        tpoPromoMetricKeys.STAND_ALONE_INC_GROSS_MARGIN_DOLLARS,
        tpoPromoMetricKeys.GROSS_MARGIN_DOLLARS,
        tpoPromoMetricKeys.STAND_ALONE_NET_PRICE,
        tpoPromoMetricKeys.NET_PRICE_INCL_MDF_PER_UNIT,
        tpoPromoMetricKeys.NET_PRICE_PER_STAT,
        tpoPromoMetricKeys.CONSUMPTION_MPIC_STAT,
        tpoPromoMetricKeys.TPO_TOTAL_UNITS,
    ];

    // console.log(
    //     'total Metrics to compute ',
    //     metricKeys.map((m) => m.toLocaleUpperCase()),
    // );

    newUpdatedPPGs.map((ppg: ProductPPG) => {
        const allMetricKpiValues: any = {};

        // Extract current KPI values from `ppgs`
        ppg.metrics.forEach((metric: Metric) => {
            allMetricKpiValues[promoConfigDictionary.promoMetrics[metric.id].name_key] = metric.value;
        });
        ppg.spendDetails.forEach((metric: Metric) => {
            allMetricKpiValues[promoConfigDictionary.spendMetrics[metric.id].name_key] = metric.value;
        });

        // console.log(`Computing for material group  -  ${ppg.name}`);

        // Loop through each metric key and process it
        metricKeys.forEach((metricKey) => {
            const formula = promoConfigDictionary.kpiFormulas[metricKey];
            let evaluatedValue: any = 0;
            // console.log('values object ', allMetricKpiValues);
            // console.log(`Computing for -  ${metricKey.toLocaleUpperCase()}`);
            // console.log(`formula -  ${formula}`);
            if (formula) {
                try {
                    const expression = formula.replace(/\b[A-Za-z_]+\b/g, (match: any) => {
                        const value = allMetricKpiValues[match];
                        return Number(value) ? value.toString() : '0';
                    });
                    // console.log(`expression -  ${expression}`);
                    evaluatedValue = parser.evaluate(expression);

                    // Check for NaN or Infinity
                    if (!isFinite(evaluatedValue) || isNaN(evaluatedValue)) {
                        evaluatedValue = 0;
                    }

                    // Update metrics and spend details with the new calculated value
                    ppg.metrics.forEach((m: Metric) => {
                        const promoMetric = promoConfigDictionary.promoMetrics[m.id];
                        if (m.id === promoMetric.id && promoMetric.name_key === metricKey) {
                            m.value = evaluatedValue;
                            // console.log(`${promoMetric.name}  = ${evaluatedValue}`);
                            allMetricKpiValues[promoMetric.name_key] = evaluatedValue;
                        }
                    });
                    ppg.spendDetails.forEach((m: Metric) => {
                        const spendMetric = promoConfigDictionary.spendMetrics[m.id];
                        if (m.id === spendMetric.id && spendMetric.name_key === metricKey) {
                            m.value = evaluatedValue;
                            // console.log(`${spendMetric.name}  = ${evaluatedValue}`);
                            allMetricKpiValues[spendMetric.name_key] = evaluatedValue;
                        }
                    });
                    // console.log('----------------------------------------------------------');
                } catch (error) {
                    console.error(`Error evaluating formula for ${metricKey}`, error);
                }
            }
        });
    });

    return newUpdatedPPGs;
};

/**
 * Validates that promo and CRA start dates are not in the past
 * @param selectedEventInfo - The promo event information
 * @returns true if dates are invalid (in the past), false otherwise
 */
export const validatePromoPastDates = (
    selectedEventInfo: PromoEvent | null,
    showNotification: boolean = true,
    datesToCompare: 'promo' | 'cra' | 'both' = 'both',
): boolean => {
    // Early return if no event info
    if (!selectedEventInfo?.currentPromoTactic) {
        return false;
    }

    const { promoStartDate, craStartDate, status, publishStatus } = selectedEventInfo.currentPromoTactic;

    // Early return if dates are not provided
    if (!promoStartDate || !craStartDate) {
        return false;
    }

    // Helper function to convert unix timestamp to dayjs object
    const convertUnixToDayjs = (unixTimestamp: number) => {
        return dayjs.unix(unixTimestamp).utc();
    };

    const promoDate = convertUnixToDayjs(promoStartDate);
    const craDate = convertUnixToDayjs(craStartDate);
    const currentDate = dayjs().utc();

    const isPromoInPast = promoDate.isBefore(currentDate, 'day');
    const isCraInPast = craDate.isBefore(currentDate, 'day');
    if (datesToCompare === 'promo') {
        return isPromoInPast;
    }
    if (datesToCompare === 'cra') {
        return isCraInPast;
    }

    if (
        isPromoInPast ||
        isCraInPast // if promo or cra is in the past
    ) {
        if (showNotification) {
            sendNotification('error', TpoMessages.error.INVALID_PROMO_DATE);
        }
        return true;
    }

    return false;
};

export const sortPPGsToExitingOrder = (existingPPGs: ProductPPG[], newPPGs: ProductPPG[]) => {
    const existingPPGsOrder = existingPPGs?.map((p: ProductPPG) => p.id) || [];

    let updatedResponse = newPPGs.sort((a, b) => {
        const indexA = existingPPGsOrder.indexOf(a.id);
        const indexB = existingPPGsOrder.indexOf(b.id);

        if (indexA === -1 && indexB === -1) return 0; // Both are new/unknown, keep original order
        if (indexA === -1) return 1; // Move `a` to the end if `a.id` is new
        if (indexB === -1) return -1; // Move `b` to the end if `b.id` is new

        return indexA - indexB;
    });

    return updatedResponse;
};

export function validateEdlpQuarterSpan(
    selectedEventInfo: PromoEvent | null,
    promoConfigDictionary: PromoConfigDictionary,
): string {
    const warnings: string[] = [];
    const currentPromo = selectedEventInfo?.currentPromoTactic;
    if (currentPromo?.promoType !== PromotionTypes.EDLP) return '';

    const miscFixedMetric = getPromoMetricByNameKeyName(promoConfigDictionary, tpoPromoMetricKeys.FIXED_MISC);
    const scanRateMetric = getPromoMetricByNameKeyName(promoConfigDictionary, tpoPromoMetricKeys.SCAN_RATE);

    const promoStart = currentPromo?.promoStartDate;
    const promoEnd = currentPromo?.promoEndDate;

    if (!promoStart || !promoEnd) return ''; // Skip if dates are not selected

    const startQuarter = getQuarter(promoStart);
    const endQuarter = getQuarter(promoEnd);

    currentPromo?.ppgs?.forEach((ppg) => {
        const miscFixed = Number(ppg.spendDetails.find((m) => m.id === miscFixedMetric.id)?.value ?? 0);
        const scanRate = Number(ppg.spendDetails.find((m) => m.id === scanRateMetric.id)?.value ?? 0);

        if ((miscFixed > 0 || scanRate > 0) && startQuarter !== endQuarter) {
            warnings.push(ppg.name ?? 'Unnamed PPG');
        }
    });

    if (warnings.length > 0) {
        return TpoMessages.info.EDLP_PPG_QUARTER_SPAN(miscFixedMetric.name, scanRateMetric.name, warnings);
    }

    return '';
}

export const resetEditableSpendsToZero = (ppgs: ProductPPG[], promoConfigDictionary: PromoConfigDictionary) => {
    ppgs.forEach((ppg: ProductPPG) => {
        ppg.spendDetails.forEach((spend: Metric) => {
            if (promoConfigDictionary.spendMetrics[spend.id]?.editable) {
                spend.value = '0';
            }
        });
    });
};

export const handleUnselectSpendMetrics = (
    selectedEventInfo: PromoEvent,
    removedMetricIds: number[],
    promoConfigDictionary: PromoConfigDictionary,
) => {
    const newUpdatedPPGs = selectedEventInfo.currentPromoTactic.ppgs;
    // store all the editable removed ids
    const removedEditableMetrics: number[] = [];
    // set value as 0 for all the removed editable ids
    newUpdatedPPGs[0]?.spendDetails.forEach((m: Metric) => {
        if (removedMetricIds.includes(m.id) && promoConfigDictionary.spendMetrics[m.id].editable) {
            removedEditableMetrics.push(m.id);
        }
    });

    const newSelectedEventInfo = _.cloneDeep(selectedEventInfo);

    // Start with the updatedPPgs
    newSelectedEventInfo.currentPromoTactic.ppgs = _.cloneDeep(newUpdatedPPGs);

    const cascadedUpdatedPPGs = newSelectedEventInfo.currentPromoTactic.ppgs.map((ppg) => {
        let updatedPPG = ppg;

        removedEditableMetrics.forEach((mId) => {
            const updatedPPGList = handleMetricInputChange(
                promoConfigDictionary,
                newSelectedEventInfo,
                mId,
                ppg.name,
                '0',
                false, // it's a metric trigger
            );

            if (Array.isArray(updatedPPGList)) {
                const foundPPG = updatedPPGList.find((p) => p.name === ppg.name);
                if (foundPPG) {
                    updatedPPG = foundPPG;
                }
            }
        });

        return updatedPPG;
    });

    newSelectedEventInfo.currentPromoTactic.ppgs = cascadedUpdatedPPGs;

    return newSelectedEventInfo;
};

//  onChangeDate function for copy event
export const onChangeDateForCopyEvent = (
    date: number | null,
    key: string,
    datesState: {
        promoStartDate: number | null;
        promoEndDate: number | null;
        craStartDate: number | null;
        craEndDate: number | null;
    },
    setDatesState: (updates: Partial<typeof datesState>) => void,
    copyValueTo?: string,
) => {
    const updates: Partial<typeof datesState> = { [key]: date };

    // If promoStartDate is changed and its greater than promo end date, then clear promo end date
    if (
        key === PromoInfoFormFieldNames.PromoStartDate &&
        date !== null &&
        Number(date) > Number(datesState.promoEndDate) &&
        Number(datesState.promoEndDate) !== 0
    ) {
        updates.promoEndDate = null;
    }

    // Handle CRA dates copy logic - copy promoStartDate to craStartDate, promoEndDate to craEndDate
    if (copyValueTo && date !== null) {
        // For promoEndDate, only copy if it's greater than or equal to craStartDate
        if (key === PromoInfoFormFieldNames.PromoEndDate) {
            if (date >= (datesState.craStartDate ?? 0)) {
                updates[copyValueTo as keyof typeof datesState] = date;
            }
        } else {
            // For promoStartDate, always copy to craStartDate
            updates[copyValueTo as keyof typeof datesState] = date;
        }
    }

    // If cra start date (direct change or copied from promo start) is greater than cra end date, clear cra end date
    const nextCraStartDate = (updates.craStartDate as number | null | undefined) ?? datesState.craStartDate;
    if (
        nextCraStartDate != null &&
        Number(nextCraStartDate) > Number(datesState.craEndDate) &&
        Number(datesState.craEndDate) !== 0
    ) {
        updates.craEndDate = null;
    }

    // Handle CRA start date validation (from getCustomOnChangeDate in PromoInfoSection)
    if (key === PromoInfoFormFieldNames.CRAStartDate && date !== null) {
        // If cra start date is greater than promo start date, show info notification
        if (Number(date) > Number(datesState.promoStartDate) && Number(datesState.promoStartDate) !== 0) {
            sendNotification('info', 'CRA Start date is greater than Promo Start Date');
        }
    }

    setDatesState(updates);
};

//  Get enabled dates for date picker - ensures end dates are after start dates

// Stored values from EpochDatePicker are UTC-midnight epochs, but EpochDatePicker.disabledDate
// interprets `enabledDates.start` in local time. Re-anchoring the stored day's calendar value to
// local midnight keeps the picker's local-time disable check on the correct calendar day across
// timezones (matches PromoInfoSection.getEnabledDates).
const getLocalEpochOfStoredDay = (storedEpoch: number) => {
    const utcDateString = dayjs.unix(storedEpoch).utc().format('MM/DD/YYYY');
    return dayjs(utcDateString, 'MM/DD/YYYY').startOf('day').unix();
};

export const getEnabledDatesForCopyEvent = (
    formField: string,
    datesState: {
        promoStartDate: number | null;
        promoEndDate: number | null;
        craStartDate: number | null;
        craEndDate: number | null;
    },
    planYear?: number,
): { start?: number; end?: number } => {
    const today = dayjs().startOf('day').unix();
    const planYearStart =
        planYear != null ? dayjs().set('year', planYear).startOf('year').startOf('day').unix() : undefined;
    const planYearEnd =
        planYear != null ? dayjs().utc().set('year', planYear).endOf('year').startOf('day').unix() : undefined;

    // Earliest date a user may pick in any copy-event field: never in the past and (when known) never before the plan year.
    const earliestSelectableDate = planYearStart != null ? Math.max(today, planYearStart) : today;

    const buildEnabledDateRange = (linkedStartDate?: number) => {
        const start =
            linkedStartDate != null ? Math.max(earliestSelectableDate, linkedStartDate) : earliestSelectableDate;
        return planYearEnd != null ? { start, end: planYearEnd } : { start };
    };

    switch (formField) {
        case 'promoStartDate':
            return buildEnabledDateRange();
        case 'promoEndDate': {
            const promoStart = datesState.promoStartDate;
            return buildEnabledDateRange(promoStart ? getLocalEpochOfStoredDay(promoStart) : undefined);
        }
        case 'craStartDate':
            return buildEnabledDateRange();
        case 'craEndDate': {
            const craStart = datesState.craStartDate;
            return buildEnabledDateRange(craStart ? getLocalEpochOfStoredDay(craStart) : undefined);
        }
        default:
            return {};
    }
};

export const validateCogsPerUnit = (
    selectedEventInfo: PromoEvent | null,
    promoConfigDictionary: PromoConfigDictionary,
) => {
    if (!selectedEventInfo?.currentPromoTactic.promoType) return '';

    const cogsPerUnitMetric = getPromoMetricByNameKeyName(promoConfigDictionary, tpoPromoMetricKeys.COGS_PER_UNIT);
    const ppgsWithMissingCogs: string[] = [];
    const typeDetails = promoConfigDictionary.promoMetricMapping[selectedEventInfo?.currentPromoTactic.promoType];

    const hasCogs = !!typeDetails?.promoMetrics[cogsPerUnitMetric.id];
    if (hasCogs) {
        selectedEventInfo?.currentPromoTactic?.ppgs
            ?.filter((ppg: ProductPPG) => ppg.isSelected)
            .forEach((ppg) => {
                const cogsValue = ppg.metrics.find((m) => m.id === cogsPerUnitMetric.id)?.value;
                if (cogsValue === null || cogsValue === undefined || Number(cogsValue) === 0) {
                    ppgsWithMissingCogs.push(ppg.name ?? 'Unnamed PPG');
                }
            });

        if (ppgsWithMissingCogs.length > 0) {
            return TpoMessages.info.COGS_PER_UNIT_MISSING_OR_ZERO(ppgsWithMissingCogs);
        }
    }
    return '';
};

export const handleTotalInputChange = (
    selectedEventInfo: PromoEvent,
    promoConfigDictionary: PromoConfigDictionary,
    metricId: number,
    productName: string,
    totalValue: string,
    updateSelectedEventProperty: (key: string, value: any) => void,
) => {
    if (!selectedEventInfo?.currentPromoTactic) return;

    const tactic = selectedEventInfo.currentPromoTactic;

    const tpoTotalUnits = getPromoMetricByNameKeyName(promoConfigDictionary, tpoPromoMetricKeys.TPO_TOTAL_UNITS);
    if (!tpoTotalUnits) return;

    const inputValue = Number(totalValue);
    if (isNaN(inputValue)) return;

    const ppgs = tactic.ppgs;
    const totalUnitsMap: Record<number, number> = {};
    let totalUnitsSum = 0;

    // Track min + max share PPG in one pass
    let extremes = {
        maxPPGId: null as number | null,
        maxValue: -Infinity,
        minPPGId: null as number | null,
        minValue: Infinity,
    };

    ppgs.forEach((ppg) => {
        const metric = ppg.metrics.find((m) => m.id === tpoTotalUnits.id);
        const units = Number(metric?.value) || 0;

        totalUnitsMap[ppg.id] = units;
        totalUnitsSum += units;

        if (units > extremes.maxValue) {
            extremes.maxValue = units;
            extremes.maxPPGId = ppg.id;
        }
        if (units < extremes.minValue) {
            extremes.minValue = units;
            extremes.minPPGId = ppg.id;
        }
    });

    console.log('Total units sum:', totalUnitsSum);
    console.log('Max PPG:', extremes.maxPPGId, 'Units:', extremes.maxValue);
    console.log('Min PPG:', extremes.minPPGId, 'Units:', extremes.minValue);

    const clonedEventInfo = _.cloneDeep(selectedEventInfo);
    const decimals = promoConfigDictionary.spendMetrics[metricId].allowedDecimals;

    const isEqualShare = totalUnitsSum === 0;
    let distributedSum = 0;

    // Step 1: Proportional (or equal) distribution
    let updatedPPGs = clonedEventInfo.currentPromoTactic.ppgs.map((ppg) => {
        const share = isEqualShare ? 1 / ppgs.length : totalUnitsMap[ppg.id] / totalUnitsSum;

        const rawValue = inputValue * share;
        const roundedValue = _.round(rawValue, decimals);
        distributedSum += roundedValue;

        console.log(
            `[PPG: ${ppg.name}] Share: ${_.round(share * 100, 2)}%, Raw: ${rawValue}, Rounded: ${roundedValue}`,
        );

        const updatedList = handleMetricInputChange(
            promoConfigDictionary,
            clonedEventInfo,
            metricId,
            ppg.name,
            roundedValue.toString(),
            false,
        );

        return Array.isArray(updatedList) ? updatedList.find((p) => p.id === ppg.id) ?? ppg : ppg;
    });

    // Step 2: Leftover calculation
    const leftover = _.round(inputValue - distributedSum, decimals);
    console.log('Leftover after distribution:', leftover);

    if (leftover !== 0) {
        const adjustPPGId = extremes.maxPPGId;

        const idx = updatedPPGs.findIndex((p) => p.id === adjustPPGId);
        if (idx !== -1) {
            const ppgObj = updatedPPGs[idx];
            const targetMetric = ppgObj.spendDetails.find((m) => m.id === metricId);

            if (targetMetric) {
                const currentVal = Number(targetMetric.value) || 0;
                const newVal = _.round(currentVal + leftover, decimals);

                console.log(`Updating PPG ${ppgObj.name}: Original=${currentVal}, New=${newVal}`);

                targetMetric.value = newVal.toString() as unknown as string;

                // Re-run handleMetricInputChange for the adjusted PPG
                const updatedList = handleMetricInputChange(
                    promoConfigDictionary,
                    clonedEventInfo,
                    metricId,
                    ppgObj.name,
                    newVal.toString(),
                    false,
                );

                if (Array.isArray(updatedList)) {
                    const updated = updatedList.find((p) => p.id === adjustPPGId);
                    if (updated) updatedPPGs[idx] = updated;
                }
            }
        }
    }

    // Step 3: Final state update
    // Spread original tactic (not clone) to keep buyGet/buyFor/buySave refs stable.
    // Clone refs would falsely trigger the promoted-SRP effect and reset it.
    updateSelectedEventProperty('currentPromoTactic', {
        ...selectedEventInfo.currentPromoTactic,
        hasChanges: true,
        ppgs: updatedPPGs,
    });
};

export const checkPromoStatus = (promoStatus: string) => {
    if (
        promoStatus === PromoStatus.CANCELLED ||
        promoStatus === PromoStatus.SUBMITTED_FOR_APPROVAL ||
        promoStatus === PromoStatus.PENDING_APPROVAL
    ) {
        return true;
    }
    return false;
};

export const hasInvalidMetricsForModelUplift = (
    promoConfigDictionary: PromoConfigDictionary,
    promoTactic: PromoTactic,
) => {
    const edlp_metric = getPromoMetricByNameKeyName(promoConfigDictionary, tpoPromoMetricKeys.EDLP_SRP);
    const percent_of_consumption_metric = getPromoMetricByNameKeyName(
        promoConfigDictionary,
        tpoPromoMetricKeys.PERCENT_OF_CONSUMPTION,
    );
    const edlp_srp_true_metric = getPromoMetricByNameKeyName(promoConfigDictionary, tpoPromoMetricKeys.EDLP_SRP_TRUE);
    const promo_srp_metric = getPromoMetricByNameKeyName(promoConfigDictionary, tpoPromoMetricKeys.PROMOTED_SRP);
    let invalidMetricName = '';
    const hasInvalidMetric = promoTactic?.ppgs.some((product) => {
        return (
            product.isSelected &&
            product.metrics.some((metric) => {
                if (
                    [
                        edlp_metric?.name,
                        percent_of_consumption_metric?.name,
                        edlp_srp_true_metric?.name,
                        promo_srp_metric?.name,
                    ].includes(metric.name) &&
                    Number(metric.value) === 0
                ) {
                    invalidMetricName = metric.name;
                    return true;
                }
                return false;
            })
        );
    });
    if (hasInvalidMetric) {
        sendNotification('error', TpoMessages.error.INVALID_VALUE(invalidMetricName, 0));
        return true;
    }
    return false;
};
